<template>
  <v-app id="inspire" class="c-bg-gray">
    <v-main>
      <v-container class="fill-height" fluid>
        <v-col cols="12" class="col-md-10 offset-md-1 col-sm-12 tw-mt-5 md:tw-mt-0">
          <v-row align="center" justify="center">
            <v-col id="reset-password" cols="12" sm="8" md="5">
              <v-card class="elevation-3 pb-5">
                <div class="tw-w-full tw-flex tw-justify-center">
                  <img src="/pacis-logo.png" class="tw-w-24 md:tw-w-32 tw-mt-4 md:tw-mt-6" alt="logo">
                </div>
                <v-card-text class="tw-px-4 md:tw-px-10">
                  <div class="tw-w-full tw-text-center tw-mt-6">
                    <h2 class="c-blue-text title tw-mb-2">Reset Password</h2>
                    <p class="tw-text-sm c-gray-text tw-mb-4">
                      Enter the OTP sent to <strong>{{ username }}</strong>
                    </p>
                  </div>
                  <v-form v-model="isValid">
                    <v-text-field
                      id="otp"
                      label="OTP Code"
                      type="number"
                      v-model="form.otp"
                      :rules="[required('OTP')]"
                    >
                      <v-icon slot="prepend" color="primary">mdi-security</v-icon>
                    </v-text-field>

                    <v-text-field
                      id="password"
                      label="New Password"
                      :type="passwordField"
                      v-model="form.password"
                      :rules="[
                        required('Password'),
                        minLength('password', 8),
                        lowerCaseFormat(),
                        upperCaseFormat(),
                        digitFormat(),
                        specialCharFormat(),
                      ]"
                    >
                      <v-icon slot="prepend" color="primary">mdi-lock</v-icon>
                      <v-icon
                        slot="append"
                        color="primary"
                        class="tw-cursor-pointer"
                        @click="passwordField = passwordField === 'password' ? 'text' : 'password'"
                      >
                        {{ passwordField === 'password' ? 'mdi-eye' : 'mdi-eye-off' }}
                      </v-icon>
                    </v-text-field>

                    <!-- ── Password Requirements Guide ── -->
                    <div class="tw-mt-1 tw-mb-4 tw-ml-8 tw-pl-2 tw-border-l-2 tw-border-gray-100">
                      <p class="tw-text-[9px] tw-font-medium tw-text-gray-400 tw-mb-2 tw-tracking-widest">PASSWORD REQUIREMENTS</p>
                      <div class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1">
                        <div class="tw-flex tw-items-center tw-gap-1.5">
                          <v-icon x-small :color="hasMinLength ? 'success' : '#cbd5e1'" class="tw-text-[10px]">{{ hasMinLength ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                          <span :class="['tw-text-[10px]', hasMinLength ? 'tw-text-green-600 tw-font-bold' : 'tw-text-gray-400']">8+ Characters</span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-1.5">
                          <v-icon x-small :color="hasUppercase ? 'success' : '#cbd5e1'" class="tw-text-[10px]">{{ hasUppercase ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                          <span :class="['tw-text-[10px]', hasUppercase ? 'tw-text-green-600 tw-font-bold' : 'tw-text-gray-400']">Uppercase (A-Z)</span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-1.5">
                          <v-icon x-small :color="hasLowercase ? 'success' : '#cbd5e1'" class="tw-text-[10px]">{{ hasLowercase ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                          <span :class="['tw-text-[10px]', hasLowercase ? 'tw-text-green-600 tw-font-bold' : 'tw-text-gray-400']">Lowercase (a-z)</span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-1.5">
                          <v-icon x-small :color="hasNumber ? 'success' : '#cbd5e1'" class="tw-text-[10px]">{{ hasNumber ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                          <span :class="['tw-text-[10px]', hasNumber ? 'tw-text-green-600 tw-font-bold' : 'tw-text-gray-400']">Number (0-9)</span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-1.5">
                          <v-icon x-small :color="hasSpecial ? 'success' : '#cbd5e1'" class="tw-text-[10px]">{{ hasSpecial ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                          <span :class="['tw-text-[10px]', hasSpecial ? 'tw-text-green-600 tw-font-bold' : 'tw-text-gray-400']">Special (!@#$%^*)</span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-1.5">
                          <v-icon x-small :color="passwordsMatch ? 'success' : '#cbd5e1'" class="tw-text-[10px]">{{ passwordsMatch ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                          <span :class="['tw-text-[10px]', passwordsMatch ? 'tw-text-green-600 tw-font-bold' : 'tw-text-gray-400']">Passwords Match</span>
                        </div>
                      </div>
                    </div>

                    <v-text-field
                      id="confirm-new-password"
                      label="Confirm New Password"
                      :type="passwordConfirmField"
                      v-model="form.confirmPassword"
                      :rules="[required('Confirm Password'), confirmPassword('Confirm Password', form.password)]"
                    >
                      <v-icon slot="prepend" color="primary">mdi-lock</v-icon>
                      <v-icon
                        slot="append"
                        color="primary"
                        class="tw-cursor-pointer"
                        @click="passwordConfirmField = passwordConfirmField === 'password' ? 'text' : 'password'"
                      >
                        {{ passwordConfirmField === 'password' ? 'mdi-eye' : 'mdi-eye-off' }}
                      </v-icon>
                    </v-text-field>
                  </v-form>
                </v-card-text>

                <v-card-actions class="col-sm-6 offset-sm-3">
                  <v-btn
                    block
                    large
                    id="submit"
                    color="secondary"
                    class="my-3"
                    @click="onUpdatePassword"
                    :disabled="!isValid"
                    :loading="loading"
                  >
                    Reset Password
                  </v-btn>
                </v-card-actions>

                <v-card-actions class="tw-w-full tw-flex tw-flex-col tw-items-center tw-gap-2">
                  <a
                    id="otp-resend"
                    class="tw-font-bold tw-cursor-pointer"
                    href="#"
                    @click.prevent="resendOtp"
                  >
                    {{ resendLabel }}
                  </a>
                </v-card-actions>

                <v-card-actions class="tw-w-full tw-flex justify-center tw-pb-10">
                  <span class="c-gray-text body-2">
                    Already have an account?
                    <router-link class="tw-font-bold" :to="{ name: 'SignIn' }">Sign In</router-link>
                  </span>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import validations from '@/utils/validations';
import helpers from '@/utils/helpers';
import { FB_RESET_PASS_COMPLETE } from '@/utils/const';

const AUTH_API = process.env.VUE_APP_AUTH_API;

export default {
  metaInfo: {
    title: 'Reset Password',
  },
  props: {
    source: String,
  },
  data() {
    return {
      form: {
        otp: '',
        password: '',
        confirmPassword: '',
      },
      username: '',
      ...validations,
      loading: false,
      resending: false,
      isValid: false,
      resendLabel: 'Resend OTP',
      passwordField: 'password',
      passwordConfirmField: 'password',
    };
  },
  computed: {
    hasMinLength() {
      return this.form.password.length >= 8;
    },
    hasUppercase() {
      return /[A-Z]/.test(this.form.password);
    },
    hasLowercase() {
      return /[a-z]/.test(this.form.password);
    },
    hasNumber() {
      return /[0-9]/.test(this.form.password);
    },
    hasSpecial() {
      return /[!@#$%^&*)(+=.,_-]/.test(this.form.password);
    },
    passwordsMatch() {
      return (
        this.form.password !== ''
        && this.form.password === this.form.confirmPassword
      );
    },
  },
  mounted() {
    const regex = /\S+@\S+\.\S+/;
    let param = this.$route.query.user;
    if (param && typeof param === 'string') {
      param = decodeURIComponent(param);
    }
    if (!param || !regex.test(param)) {
      this.$toast.error('Invalid address, please request a reset code again.', 'Error');
      this.$router.push({ name: 'ForgotPassword' });
      return;
    }
    this.username = param;
  },
  methods: {
    // ── Confirm OTP + new password ──────────────────────────
    async onUpdatePassword() {
      this.loading = true;
      try {
        const res = await fetch(`${AUTH_API}/auth/confirm-reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: this.username,
            code: String(this.form.otp).trim(),
            newPassword: this.form.password,
          }),
        });

        const data = await res.json();

        if (!res.ok) {
          this.$toast.error(data.message || 'Reset failed. Please try again.', 'Error');
          return;
        }

        this.$analytics.logEvent(FB_RESET_PASS_COMPLETE);
        this.$toast.success(data.message, 'Success');
        helpers.clearForm(this.form);
        this.$router.push({ name: 'SignIn' });
      } catch (err) {
        this.$toast.error('Network error. Please try again.', 'Error');
      } finally {
        this.loading = false;
      }
    },

    // ── Resend OTP via forgot-password endpoint ─────────────
    async resendOtp() {
      if (this.resending) return;
      this.resending = true;
      this.resendLabel = 'Resending...';
      try {
        const res = await fetch(`${AUTH_API}/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.username }),
        });

        const data = await res.json();

        if (!res.ok) {
          this.$toast.error(data.message || 'Could not resend OTP.', 'Error');
        } else {
          this.$toast.success('OTP resent. Check your email.', 'Success');
        }
      } catch (err) {
        this.$toast.error('Network error. Please try again.', 'Error');
      } finally {
        this.resending = false;
        this.resendLabel = 'Resend OTP';
      }
    },
  },
};
</script>
